// =============================================================================
// tokens.ts — Fortius global design tokens (single source of truth)
//
// FORT-179 — the foundation of the design-system epic (FORT-178).
//
// This is PURE DATA: a plain TypeScript object with no React, Ant Design, or
// DOM imports. That is deliberate — the tokens layer must be importable by:
//   * Ant Design's `ConfigProvider` (admin web theme),
//   * the upcoming Tier 2 primitives (FORT-182) and Tier 3 components (FORT-183),
//   * and a future React Native app **unchanged** (no web-only types here).
//
// Colour / spacing / typography literals are scattered across ~440 inline styles
// today. New components should read from these tokens instead of hardcoding raw
// values; existing screens migrate incrementally (HomePage is the first proof of
// concept). Tenant branding overrides flow in on top of these tokens via the
// token-building layer added in FORT-180 — components never read `branding.*`
// directly once that lands.
//
// The default palette is intentionally aligned with Ant Design's own defaults
// (colorError #ff4d4f, colorSuccess #52c41a, colorWarning #faad14, borderRadius
// 6, fontSize 14) so wiring these into `ConfigProvider` changes the source of
// truth without changing the rendered result. `primary` is the Fortius navy,
// matching the long-standing `#00005a` fallback in App.tsx.
// =============================================================================

export const tokens = {
  color: {
    primary: '#00005a',
    danger: '#ff4d4f',
    success: '#52c41a',
    warning: '#faad14',
    text: { primary: '#1f1f1f', secondary: '#595959', disabled: '#bfbfbf' },
    background: { default: '#ffffff', muted: '#fafafa' },
    border: { default: '#d9d9d9' },
    // Sider chrome colours. Defaults match the long-standing App.tsx fallbacks;
    // tenant branding overrides them via the token-building layer (buildTokens).
    sidebar: { bg: '#00005a', submenuBg: '#161b38', itemSelectedBg: '#2a305c' },
  },
  // 4px base scale. Keys are step indices, values are pixels.
  space: { 0: 0, 1: 4, 2: 8, 3: 12, 4: 16, 5: 24, 6: 32, 7: 48 },
  radius: { none: 0, sm: 2, md: 6, lg: 8, full: 9999 },
  fontSize: { xs: 12, sm: 14, md: 16, lg: 18, xl: 24, '2xl': 30 },
  fontWeight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 12px rgba(0, 0, 0, 0.10)',
  },
} as const;

export type Tokens = typeof tokens;
